import { NextIntlClientProvider } from 'next-intl';
import en from '../../../messages/en.json';
import { designSystemTranslations } from '@terra/ui-i18n';

type DesignSystemFindingDrawerOverrides = {
  findings: { findingDrawer: Record<string, string> };
};

function isRecord(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

function isStringRecord(obj: unknown): obj is Record<string, string> {
  if (typeof obj !== 'object' || obj === null) return false;
  return Object.values(obj).every((value) => typeof value === 'string');
}

function getProxyFindingDrawerOverrides(
  messages: unknown,
): DesignSystemFindingDrawerOverrides | null {
  if (!isRecord(messages)) return null;
  const designSystem = messages['designSystem'];
  if (!isRecord(designSystem)) return null;
  const findings = designSystem['findings'];
  if (!isRecord(findings)) return null;
  const findingDrawer = findings['findingDrawer'];
  if (!isStringRecord(findingDrawer)) return null;
  return { findings: { findingDrawer } };
}

const baseDesignSystem = designSystemTranslations.en.designSystem;
const proxyOverrides = getProxyFindingDrawerOverrides(en);

const designSystem =
  proxyOverrides !== null
    ? {
        ...baseDesignSystem,
        findings: {
          ...baseDesignSystem?.findings,
          findingDrawer: {
            ...baseDesignSystem?.findings?.findingDrawer,
            ...proxyOverrides.findings.findingDrawer,
          },
        },
      }
    : baseDesignSystem;

export const messages = {
  en: {
    ...en,
    designSystem,
  },
} as const;

export type Locale = keyof typeof messages;

interface IntlProviderProps {
  children: React.ReactNode;
  locale?: Locale;
}

export function UseIntlProvider({
  children,
  locale = 'en',
}: IntlProviderProps) {
  return (
    <NextIntlClientProvider
      messages={messages[locale as keyof typeof messages]}
      locale={locale}
      timeZone="UTC"
    >
      {children}
    </NextIntlClientProvider>
  );
}
